import type { AnalyticsBrowser } from '@dreamdata/analytics-next'

/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { Cycle, Plan, Version } from '@src/app/billing/pricing/PricingController'
/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { GreetingOption } from '@src/app/command/SetGreetingCommand'
/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { ContactFilterType } from '@src/app/contacts/filters'
/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { FirstRunInboxStep } from '@src/app/onboarding/FirstRunInbox/FirstRunInboxUIState'
/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { CallRecordingNotificationType } from '@src/app/settings/phone-number/CallRecording/CallRecording'
import { logError } from '@src/lib/log'
import { isTollFreePhoneNumber } from '@src/lib/phone-number'
import type { HoldMusicGenre } from '@src/service/dto/response/AccountHoldMusicGetByPhoneNumberIdResponse'
import type { ReferralSource } from '@src/service/model/OrganizationModel'
import type {
  RingOrderSkipUsersMode,
  RingOrderType,
} from '@src/service/model/RingOrderModel'
import type { AddonCreditsTier } from '@src/service/model/addon/AddonTier'
import type { ContactItemType } from '@src/service/model/contact/ContactItemModel'
import type { AvailablePhoneNumber } from '@src/service/transport/account'
import type { TextToSpeechVoiceType } from '@src/service/transport/communication'

// eslint-disable-next-line import-x/no-restricted-paths
import { OdysseyAnalyticsBridge } from '@odyssey/analytics/OdysseyAnalyticsBridge'

/**
 * The features, prompts, and actions that can trigger a user to upgrade their plan
 */
export type PlanUpgradeTrigger =
  // From the billing page or billing elements:
  | 'billing page callout'
  | 'plans page'
  | 'trial card'
  // From a url param:
  | 'url'
  // Business plan features that first open the upgrade modal:
  | 'analytics'
  | 'attio integration'
  | 'hubspot integration'
  | 'jobber integration'
  | 'make integration'
  | 'chatgpt integration'
  | 'claude integration'
  | 'grokbot integration'
  | 'clio integration'
  | 'highlevel integration'
  | 'housecallpro integration'
  | 'jobtread integration'
  | 'monday integration'
  | 'pipedrive integration'
  | 'salesfoce integration'
  | 'gong integration'
  | 'zoho integration'
  | 'ring order'
  | 'auto recording'
  | 'call transcriptions'
  | 'group calling'
  | 'call transfer'
  | 'phone menu'
  | 'call assistant'
  // Workflow builder features
  | 'workflow banner publish'
  | 'workflow banner upgrade'
  | 'workflow publish during trial upgrade'
  // Workflow steps
  | 'workflow phone menu'
  | 'workflow ring users'
  | 'workflow ring users single'
  | 'workflow ring users custom'
  | 'workflow play audio'
  | 'workflow forward call'
  | 'workflow branch'
  // TODO(WFA-1243): Remove this once the voice agent upgrade code is removed,
  // since the voice agent step no longer actually requires an "upgrade"
  | 'workflow voice agent'
  // Legacy Business plan features (only available to legacy users):
  | 'phone menu destination'
  | 'phone menu default action'
  // Tasks
  | 'open task limit'
  // Fallback value, shouldn't happen in practice:
  | 'unknown'

/**
 * The source of a plan upgrade trigger.
 *
 * This is used to further clarify the exact location of a plan upgrade trigger since
 * some triggers can be located in multiple places.
 */
export type PlanUpgradeSource =
  | 'workflow draggable list block'
  | 'workflow canvas block'
  | 'workflow step side panel'
  | 'workflow ring order type select'
  // Task open-task-limit entry points
  | 'task conversation'
  | 'task phone channel inbox'
  | 'task conversation activity'
  | 'task organization'
  | 'task reopen'

export type PlanUpgradeAnalyticsData = {
  trigger: PlanUpgradeTrigger
  source?: PlanUpgradeSource
}

export type BusinessHoursToggleSource = 'phone number settings' | 'workflow builder'

export type PricingCycleToggleSource =
  | 'billing_settings'
  | 'onboarding'
  | 'marketing_pricing_page'

type SaveFirstRunInboxStepParams =
  | {
      usersInvited: boolean
      totalInvitesSent: number
    }
  | {
      userCustomizedInbox: boolean
    }
  | {
      aiContactSuggestions: boolean
      callTranscription: boolean
      autoRecordCalls: boolean
    }

type PhoneNumberSearchType = 'city' | 'area-code' | 'toll-free'

export default class WorkspaceAnalyticsStore {
  private readonly analytics: typeof OdysseyAnalyticsBridge

  constructor(private readonly dreamdata: AnalyticsBrowser) {
    this.analytics = OdysseyAnalyticsBridge
  }

  /**
   * Tracks an event in Google Tag Manager if tracking is enabled
   * @param event - The event data to track
   */
  private trackGTM(event: { event: string; [key: string]: unknown }) {
    window.dataLayer?.push(event)
  }

  trackPageView() {
    this.dreamdata.page().catch(logError)
  }

  /**
   * Tracks when a user has logged in
   *
   * @param method - Method used to log in
   */
  login(method: 'password' | 'passwordless' | 'google' | 'apple') {
    this.trackGTM({
      event: 'login',
      method,
    })

    this.dreamdata.track('form submission - login', { method }).catch(logError)

    void this.analytics.track('login', { method })
  }

  /**
   * Tracks when a user has signed up
   *
   * @param method - Method used to sign up
   * @param trackGTM - Whether to track this event in Google Tag Manager
   * @param email - Email address to include in GTM tracking
   */
  signup(
    method: 'passwordless' | 'google' | 'apple',
    trackGTM = false,
    email?: string | null,
  ) {
    if (trackGTM) {
      this.trackGTM({
        event: 'signup',
        method,
        ...(email ? { email } : {}),
      })
    }

    this.dreamdata.track('form submission - signup', { method }).catch(logError)

    void this.analytics.track('signup', { method })
  }

  /**
   * Tracks when a user has created a Workspace
   */
  createdWorkspace() {
    this.trackGTM({
      event: 'create_workspace',
    })

    void this.analytics.track('create_workspace')
  }

  /**
   * Tracks when a user has started a trial
   */
  startedTrial(trackGTM = false) {
    if (trackGTM) {
      this.trackGTM({
        event: 'start_trial',
      })
    }

    void this.analytics.track('start_trial')
  }

  /**
   * Tracks when a user has switched manually from trial to paid
   */
  switchedToPaid() {
    void this.analytics.track('switched_to_paid')
  }

  /**
   * Tracks when a user has opened the Business trial modal
   *
   * @param trigger - Where the modal was opened from
   * @param source - The source of the trial modal open trigger
   */
  businessTrialModalOpened({ trigger, source }: PlanUpgradeAnalyticsData) {
    void this.analytics.track('business_trial_modal_opened', { trigger, source })
  }

  /**
   * Tracks when a user clicks the button to start the Business trial
   *
   * @param trigger - Where the modal was opened from when the trial was started
   * @param source - The source of the trial start trigger
   */
  businessTrialStarted({ trigger, source }: PlanUpgradeAnalyticsData) {
    void this.analytics.track('business_trial_started', { trigger, source })
  }

  /**
   * Tracks when a user has added payment info
   */
  addedPaymentInfo({ paymentSource }: { paymentSource: string }) {
    this.trackGTM({
      event: 'payment_info_added',
      payment_source: paymentSource,
    })

    void this.analytics.track('payment_info_added', { payment_source: paymentSource })
  }

  /**
   * Tracks when a user has updated payment info
   */
  updatedPaymentInfo({ paymentSource }: { paymentSource: string }) {
    this.trackGTM({
      event: 'payment_info_updated',
      payment_source: paymentSource,
    })

    void this.analytics.track('payment_info_updated', { payment_source: paymentSource })
  }

  /**
   * Tracks when the user has viewed the plans comparison inside the billing settings
   */
  billingPlansViewed() {
    void this.analytics.track('billing_plans_viewed')
  }

  /**
   * Tracks when a user switches the pricing view between monthly and yearly.
   *
   * @param cycle - The billing cycle the user switched to
   * @param source - The pricing surface the toggle was used on
   * @param pricingVersion - The pricing version backing the prices on screen
   * @param cataloguePricing - Whether the prices on screen come from the GraphQL
   *   catalogue rather than the client-side price matrix reported here
   * @param prices - Per user, per month price shown for each plan on screen
   */
  pricingCycleToggled({
    cycle,
    source,
    pricingVersion,
    cataloguePricing,
    prices,
  }: {
    cycle: Cycle
    source: PricingCycleToggleSource
    pricingVersion: Version
    cataloguePricing: boolean
    prices: Partial<Record<Plan, number>>
  }) {
    void this.analytics.track('pricing_cycle_toggled', {
      cycle,
      source,
      pricing_version: pricingVersion,
      catalogue_pricing: cataloguePricing,
      price_standard: prices.standard,
      price_premium: prices.premium,
      price_scale: prices.scale,
    })
  }

  /**
   * Tracks when a user has upgraded their billing plan
   *
   * @param fromPlan - The plan the user has upgraded from
   * @param toPlan - The plan the user has upgraded to
   * @param trigger - The trigger that caused the user to upgrade their plan
   * @param source - The source of the plan upgrade trigger
   */
  billingPlanUpgraded({
    fromPlan,
    toPlan,
    trigger,
    source,
  }: PlanUpgradeAnalyticsData & {
    fromPlan: Plan
    toPlan: Plan
  }) {
    void this.analytics.track('billing_plan_upgraded', {
      from_plan: fromPlan,
      to_plan: toPlan,
      trigger,
      source,
    })
  }

  /**
   * Tracks when a user has invited someone
   */
  invited() {
    this.trackGTM({
      event: 'invite',
    })

    void this.analytics.track('invite')
  }

  /**
   * Tracks when a user has accepted an invite to join a workspace
   *
   * @param companyName - Name of the company/organization that owns the workspace
   */
  userAcceptedInvite(companyName?: string) {
    void this.analytics.track('user_accepted_invite', { companyName })
  }

  /**
   * Tracks when a phone number is added to the workspace
   *
   * @param orgId
   * @param phoneNumber
   * @param type - Whether the number was added to be used by a member or as a workspace inbox
   */
  numberCreated(orgId: string, phoneNumber: string, type: 'member' | 'workspace') {
    const numberType = isTollFreePhoneNumber(phoneNumber) ? 'toll-free' : 'local'
    void this.analytics.track('number_created', { orgId, phoneNumber, type, numberType })
  }

  /**
   * Tracks when a user is added to an inbox, including the first user that is added when the inbox is created for the first time.
   *
   * @param phoneNumberId
   * @param inboxUserCount - Number of users sharing the inbox
   * @param inboxRole - Role assigned to the user within the inbox
   */
  userAddedToInbox(phoneNumberId: string, inboxUserCount: number, inboxRole: string) {
    void this.analytics.track('user_added_to_inbox', {
      phoneNumberId,
      inboxUserCount,
      inboxRole,
    })
  }

  /**
   * Tracks when a phone number is removed from the workspace
   *
   * @param orgId
   * @param phoneNumber
   */
  numberDeleted(orgId: string, phoneNumber: string): void {
    void this.analytics.track('number_deleted', { orgId, phoneNumber })
  }

  /**
   * Tracks when a porting request has been initiated
   *
   * @param orgId
   * @param phoneNumber
   * @param isResubmitting - Whether the port is being resubmitted
   */
  portInitiated(orgId: string, phoneNumber: string, isResubmitting: boolean): void {
    void this.analytics.track('port_initiated', { orgId, phoneNumber, isResubmitting })
  }

  /**
   * Tracks when a porting request has failed
   *
   * @param orgId
   * @param phoneNumber
   * @param errorCode
   * @param errorMessage
   */
  portFailed(
    orgId: string,
    phoneNumber: string,
    errorCode: string,
    errorMessage: string,
  ): void {
    void this.analytics.track('port_failed', {
      orgId,
      phoneNumber,
      errorCode,
      errorMessage,
    })
  }

  /**
   * Tracks when a user has cancelled the trial or subscription
   */
  cancellation() {
    this.trackGTM({
      event: 'cancellation',
    })

    void this.analytics.track('cancellation')
  }

  referralClicked(
    action:
      | 'settings_tab_clicked'
      | 'account_menu_tab_clicked'
      | 'side_menu_cta_clicked'
      | 'profile_cta_clicked'
      | 'open_new_window_button_clicked'
      | 'copy_link_button_clicked'
      | 'link_copied'
      | 'partner_apply_now_button_clicked'
      | 'partner_learn_more_button_clicked',
  ): void

  referralClicked(
    action: 'social_button_clicked',
    network: 'facebook' | 'twitter' | 'linkedin' | 'email',
  ): void

  /**
   * Tracks when a user clicks something related to referrals
   *
   * @param action
   * @param network
   */
  referralClicked(action: string, network?: string): void {
    void this.analytics.track('referral_clicked', { action, network })
  }

  /**
   * Tracks when a user clicks on the help and support menu
   *
   * @param action
   */
  helpAndSupportClicked(action: string) {
    void this.analytics.track('help_and_support_clicked', { action })
  }

  /**
   * Tracks when a user clicks on the Quo Academy button
   */
  quoAcademyClicked() {
    void this.analytics.track('quo_academy_clicked')
  }

  /**
   * Tracks when a user toggles the dyslexia mode
   *
   * @param enabled
   */
  dyslexiaModeToggled(enabled: boolean) {
    void this.analytics.track('dyslexia_mode_toggled', { enabled })
  }

  /**
   * Tracks when the user filters the contacts
   *
   * @param filter
   * @param totalFilters
   */
  contactsFiltered(filter: ContactFilterType, totalFilters: number) {
    void this.analytics.track('contacts_filtered', {
      filter,
      total_filters: totalFilters,
    })
  }

  /**
   * Tracks when a contact filter has been cleared
   *
   * @param filter
   * @param totalFilters
   */
  contactsFilterCleared(filter: ContactFilterType, totalFilters: number) {
    void this.analytics.track('contacts_filter_cleared', { filter, totalFilters })
  }

  /**
   * Tracks when the contacts filter is collapsed
   *
   * @param totalFilters
   */
  contactsFiltersCollapsed(totalFilters: number) {
    void this.analytics.track('contacts_filters_collapsed', { totalFilters })
  }

  /**
   * Tracks when the contacts filter is expanded
   *
   * @param totalFilters
   */
  contactsFiltersExpanded(totalFilters: number) {
    void this.analytics.track('contacts_filters_expanded', { totalFilters })
  }

  /**
   * Tracks when a contact is created
   */
  contactCreated() {
    void this.analytics.track('contact_created')
  }

  /**
   * Tracks when a contact is updated
   */
  contactUpdated() {
    void this.analytics.track('contact_updated')
  }

  /**
   * Tracks when a contact note is created
   */
  contactNoteCreated() {
    void this.analytics.track('contact_note_created')
  }

  /**
   * Tracks when the default contact sharing settings have been changed
   */
  defaultContactSharingChanged() {
    void this.analytics.track('default_contact_sharing_changed')
  }

  /**
   * Tracks when Google contact integration is connected successfully
   */
  googleContactsIntegrationConnected() {
    void this.analytics.track('google_contacts_integration_connected')
  }

  /**
   * Tracks when an integration button is clicked
   *
   * @param name
   */
  integrationMade(name: string) {
    void this.analytics.track('integration_made', { name })
  }

  /**
   * Tracks when user successfully imports contact CSV
   */
  contactsCsvImported() {
    void this.analytics.track('contacts_csv_imported')
  }

  /**
   * Tracks when a user creates a new custom property on a contact
   *
   * @param customFieldType
   */
  customContactPropertyCreated(customFieldType: ContactItemType) {
    void this.analytics.track('custom_contact_property_created', { customFieldType })
  }

  /**
   * Tracks when an auto reply is saved
   *
   * @param phoneNumberId
   * @param autoReplyType
   */
  autoReplySaved(phoneNumberId: string, autoReplyType: string) {
    void this.analytics.track('auto_reply_saved', { phoneNumberId, autoReplyType })
  }

  /**
   * Tracks when a user enables the phone menu on a number
   *
   * @param phoneNumberId
   * @param enabled
   * @param menuOptions - Number of menu options created
   */
  phoneMenuToggled(phoneNumberId: string, enabled: boolean, menuOptions: number) {
    void this.analytics.track('phone_menu_toggled', {
      phoneNumberId,
      enabled,
      menuOptions,
    })
  }

  /**
   * Tracks when a user selects an option for the after hours call forwarding
   *
   * @param type - The option selected by the user
   * @param phoneNumberSelectionType - The type of phone number selected by the user in the case of a call forwarding
   */
  afterHoursCallForwardChanged(
    type: 'external' | 'internal' | 'playAudio' | 'voicemail',
    phoneNumberSelectionType:
      | 'add-number'
      | 'contact'
      | 'member'
      | 'inbox'
      | 'inbox-member'
      | 'number'
      | null,
  ) {
    void this.analytics.track('after_hours_call_fwd_changed', {
      type,
      phoneNumberSelectionType,
    })
  }

  /**
   * Tracks when a user selects an option for the unanswered calls forwarding
   *
   * @param type - The option selected by the user
   * @param phoneNumberSelectionType - The type of phone number selected by the user in the case of a call forwarding
   */
  unansweredCallForwardChanged(
    type: 'external' | 'internal' | 'playAudio' | 'voicemail',
    phoneNumberSelectionType:
      | 'add-number'
      | 'contact'
      | 'member'
      | 'inbox'
      | 'inbox-member'
      | 'number'
      | null,
  ) {
    void this.analytics.track('unanswered_call_fwd_changed', {
      type,
      phoneNumberSelectionType,
    })
  }

  /**
   * Tracks when a user changes the ring order type
   *
   * @param phoneNumberId
   * @param ringOrderType
   */
  ringOrderTypeChanged(phoneNumberId: string, ringOrderType: RingOrderType) {
    void this.analytics.track('ring_order_type_changed', { phoneNumberId, ringOrderType })
  }

  /**
   * Tracks when a user switches the "only ring users that are not on a call"
   * setting on a ring order on or off.
   *
   * @param phoneNumberId
   * @param skipUsersMode
   */
  ringOrderSkipUsersToggled(
    phoneNumberId: string,
    skipUsersMode: RingOrderSkipUsersMode,
  ) {
    void this.analytics.track('ring_order_skip_users_toggled', {
      phoneNumberId,
      skipUsersMode,
    })
  }

  /**
   * Tracks when a user saves a custom voicemail
   *
   * @param phoneNumberId
   * @param voicemailType
   */
  customVoicemailSaved(phoneNumberId: string, voicemailType: GreetingOption) {
    void this.analytics.track('custom_voicemail_saved', { phoneNumberId, voicemailType })
  }

  /**
   * Tracks when a user toggles the business hours on a phone number
   *
   * @param phoneNumberId
   * @param enabled
   * @param source - The source of the toggle, either from the workflow builder or the phone number settings
   */
  businessHoursToggled(
    phoneNumberId: string,
    enabled: boolean,
    source: 'workflow builder' | 'phone number settings',
  ) {
    void this.analytics.track('business_hours_toggled', {
      phoneNumberId,
      enabled,
      source,
    })
  }

  /**
   * Tracks when a new user group is created
   *
   * @param numberOfUsers
   * @param name
   */
  userGroupCreated(numberOfUsers: number, name: string) {
    void this.analytics.track('user_group_created', { numberOfUsers, name })
  }

  /**
   * Tracks when a user is added to a member group after it was already created
   */
  userAddedToGroup() {
    void this.analytics.track('user_added_to_group')
  }

  /**
   * Tracks when a user toggles auto call recording from the phone number settings
   *
   * @param phoneNumberId
   * @param enabled
   */
  autoCallRecordingToggled(phoneNumberId: string, enabled: boolean) {
    void this.analytics.track('auto_call_recording_toggled', { phoneNumberId, enabled })
  }

  /**
   * Tracks when a user toggles auto call recording from the phone number settings
   *
   * @param phoneNumberId
   * @param enabled
   */
  callRecordingNotificationToggled(phoneNumberId: string, enabled: boolean) {
    void this.analytics.track('call_recording_notification_toggled', {
      phoneNumberId,
      enabled,
    })
  }

  /**
   * Tracks when a user changes their call recording disclosure options from the phone number settings
   *
   * @param phoneNumberId
   * @param settings
   */
  callRecordingNotificationOptions(
    phoneNumberId: string,
    callRecordingNotificationType: CallRecordingNotificationType,
  ) {
    void this.analytics.track('call_recording_notification_options', {
      phoneNumberId,
      callRecordingNotificationType,
    })
  }

  /**
   * Tracks when a user toggles call transcription and summary in the phone number settings
   *
   * @param phoneNumberId
   * @param enabled
   */
  callTranscriptionToggled(phoneNumberId: string, enabled: boolean) {
    void this.analytics.track('call_transcription_toggled', { phoneNumberId, enabled })
  }

  /**
   * Tracks when a user toggles international calling and messaging in the phone number settings
   *
   * @param phoneNumberId
   * @param enabled
   */
  internationalCallingMessagingToggled(phoneNumberId: string, enabled: boolean) {
    void this.analytics.track('international_calling_messaging_toggled', {
      phoneNumberId,
      enabled,
    })
  }

  /**
   * Tracks when call forwarding is toggled in the phone number settings
   *
   * @param phoneNumberId
   * @param enabled
   */
  callForwardingToggled(phoneNumberId: string, enabled: boolean) {
    void this.analytics.track('call_forwarding_toggled', { phoneNumberId, enabled })
  }

  /**
   * Tracks when a user clicks on the "Copy invite link" button
   */
  inviteLinkCopied() {
    void this.analytics.track('invite_link_copied')
  }

  /**
   * Tracks when a user clicks on the "Pick a different number" button in the phone number step of the onboarding
   */
  phoneNumberPickerOpened() {
    void this.analytics.track('phone_number_picker_opened')
  }

  /**
   * Tracks when a user picks a different phone number than the one they got assigned in the phone number step of the onboarding
   */
  phoneNumberPicked(phoneNumber: AvailablePhoneNumber) {
    void this.analytics.track('phone_number_picked', { ...phoneNumber })
  }

  /**
   * Tracks when a user dismisses the mobile app prompt
   */
  mobileAppPromptDismissed() {
    void this.analytics.track('app_prompt_dismissed')
  }

  /**
   * Tracks when a user clicks the button to get the mobile app
   */
  mobileAppPromptTapped() {
    void this.analytics.track('app_prompt_tapped')
  }

  /**
   * Tracks when the ai contact suggestion side panel gets displayed
   */
  aiContactSuggestionDisplayed(suggestedFieldsKeys: string[]) {
    void this.analytics.track('ai_contact_suggestion_displayed', { suggestedFieldsKeys })
  }

  /**
   * Tracks when the ai contact suggestion is accepted
   */
  aiContactSuggestionAccepted(suggestedFieldsKeys: string[]) {
    void this.analytics.track('ai_contact_suggestion_accepted', { suggestedFieldsKeys })
  }

  /**
   * Tracks when the ai contact suggestion is dismissed
   */
  aiContactSuggestionDismissed(suggestedFieldsKeys: string[]) {
    void this.analytics.track('ai_contact_suggestion_dismissed', { suggestedFieldsKeys })
  }

  /**
   * Tracks when the ai contact suggestion is edited
   */
  aiContactSuggestionEdited(editedFieldsKeys: string[], suggestedFieldsKeys: string[]) {
    void this.analytics.track('ai_contact_suggestion_edited', {
      suggestedFieldsKeys,
      editedFieldsKeys,
    })
  }

  /**
   * Tracks when the command to add members to an inbox is opened
   * @param from - Where the command was opened from
   */
  shareInboxCommandOpened(from: 'inboxMenu' | 'settings') {
    void this.analytics.track('share_inbox_command_opened', { from })
  }

  /**
   * Tracks when a user skips a step in the first run inbox flow
   * @param stepName The name of the step they skipped
   */
  skipFirstRunInboxStep(stepName: string) {
    void this.analytics.track('skip_first_run_inbox_step', { stepName })
  }

  /**
   * Tracks when a user saves changes on a step in the first run inbox flow
   * @param stepName The name of the step they saved
   * @param params Attributes we want to attach to the event
   */
  saveFirstRunInboxStep(
    stepName: FirstRunInboxStep,
    params: SaveFirstRunInboxStepParams,
  ) {
    void this.analytics.track('save_first_run_inbox_step', { stepName, ...params })
  }

  /**
   * Tracks when the inbox dropdown inside the invite modal is expanded
   */
  inviteModalInboxDropdownExpanded() {
    void this.analytics.track('invite_modal_inbox_dropdown_expanded')
  }

  /**
   * Tracks when the role dropdown inside the invite modal is expanded
   */
  inviteModalRoleDropdownExpanded() {
    void this.analytics.track('invite_modal_role_dropdown_expanded')
  }

  /**
   * Tracks when invites are sent from the invite modal
   * @param newNumberRemoved
   * @param sharedInboxSelected
   */

  invitesSent({
    newNumberRemoved,
    sharedInboxSelected,
  }: {
    newNumberRemoved: boolean
    sharedInboxSelected: boolean
  }) {
    void this.analytics.track('invites_sent', { newNumberRemoved, sharedInboxSelected })
  }

  /**
   * Tracks when a user clicks the button to show the tax details
   */
  showTaxDetailsButtonClicked() {
    void this.analytics.track('tax_details_button_clicked')
  }

  noNumbersFound({
    searchType,
    city,
    region,
    country,
    areaCode,
    filter,
  }: {
    searchType: PhoneNumberSearchType
    city?: string
    region?: string
    country?: string
    areaCode?: string | null
    filter: string | null
  }) {
    void this.analytics.track('no_numbers_found', {
      searchType,
      city,
      region,
      country,
      areaCode,
      filter,
    })
  }

  // Tracks when a user clicks the View button in the invoice list
  invoiceViewed(invoiceNumber: string) {
    void this.analytics.track('invoice_viewed', { invoiceNumber })
  }

  /**
   * Tracks when the AI agent subscription is cancelled
   */
  aiAgentSubscriptionCancelled({ usage, limit }: { usage: number; limit: number }) {
    void this.analytics.track('ai_agent_subscription_cancelled', { usage, limit })
  }

  /**
   * Tracks when the AI agent trial is cancelled
   */
  aiAgentTrialCancelled() {
    void this.analytics.track('ai_agent_trial_cancelled')
  }

  /**
   * Tracks when a user starts an unlocked trial
   *
   * @param trigger - Where the trial was started from
   * @param source - The source of the trial start trigger
   */
  unlockedTrialStarted({ trigger, source }: PlanUpgradeAnalyticsData) {
    void this.analytics.track('unlocked_trial_started', { trigger, source })
  }

  /**
   * Tracks the value a user selects for the referral source field in onboarding
   * @param referralSource The referral source the user chose
   * @param referralSourceOther The referral source user wrote
   */
  trackReferralSource(
    referralSource?: ReferralSource | null,
    referralSourceOther?: string,
  ) {
    void this.analytics.track('onboarding_referral_source', {
      referralSource,
      referralSourceOther,
    })
  }

  /**
   * Tracks when a user opens the search command
   */
  searchCommandOpened() {
    void this.analytics.track('search_command_opened')
  }

  /**
   * Tracks when the user has filled in business information and provided a website
   */
  websiteCompleted(data: {
    user_id: string
    website_provided: boolean
    business_name: string
    industry?: string
  }) {
    void this.analytics.track('onboarding_business_website_completed', data)
  }

  /**
   * Tracks when the user has filled in business information and provided a website
   */
  onboardingAiAgentDecision(data: {
    user_id: string
    test_sona: boolean
    decision: 'voice_only' | 'sms_only' | 'both_activated' | 'both_disabled'
    business_website_provided: boolean
  }) {
    void this.analytics.track('onboarding_ai_agent_wizard_decision', data)
  }

  /**
   * Tracks when a user enables or disables overages for automation credits
   */
  automationCreditsOveragesToggled(enabled: boolean) {
    void this.analytics.track('automation_credits_overages_toggled', { enabled })
  }

  /**
   * Tracks when a user changes the automation credits plan
   */
  automationCreditsPlanChanged(newTier: AddonCreditsTier) {
    void this.analytics.track('automation_credits_plan_changed', { newTier })
  }

  /**
   * Tracks when a user clicks the automation credits top banner
   */
  automationCreditsBannerClicked(plan: AddonCreditsTier) {
    void this.analytics.track('automation_credits_banner_clicked', { plan })
  }

  /**
   * Tracks when a user clicks the LaunchDarkly-configured custom banner.
   * @param banner the `analytics` identifier from the banner's flag payload
   */
  customBannerClicked(banner: string) {
    void this.analytics.track('custom_banner_clicked', { banner })
  }

  /**
   * Tracks when a user clicks the background-call-audio (autoplay) reminder
   * banner to open its instructions.
   */
  backgroundCallAudioBannerClicked() {
    void this.analytics.track('background_call_audio_banner_clicked')
  }

  /**
   * Tracks when a user uploads a custom hold music track
   */
  customHoldMusicUploaded() {
    void this.analytics.track('custom_hold_music_uploaded')
  }

  /**
   * Tracks when a user uploads a custom hold music track
   * @param genre hold track genre i.e. custom, upbeat or relaxing
   */
  holdMusicSelected(genre: HoldMusicGenre) {
    void this.analytics.track('hold_music_selected', { genre })
  }

  /**
   * Tracks when a user selects a voice in the text-to-speech modal
   * @param voice The selected voice
   */
  textToSpeechVoiceSelected(voice: TextToSpeechVoiceType) {
    void this.analytics.track('text_to_speech_voice_selected', { voice })
  }

  /**
   * Tracks when a user generates a text-to-speech audio
   * @param voice The selected voice
   */
  textToSpeechGenerated(voice: TextToSpeechVoiceType) {
    void this.analytics.track('text_to_speech_generated', { voice })
  }

  /**
   * Tracks when a user saves a text-to-speech audio
   * @param voice The selected voice
   */
  textToSpeechSaved(voice: TextToSpeechVoiceType) {
    void this.analytics.track('text_to_speech_saved', { voice })
  }

  /**
   * Tracks when a user selects a voice in the Play Audio step of the Call Flow Builder
   */
  playAudioVoiceSelected(voiceName: string) {
    void this.analytics.track('play_audio_voice_selected', { voice_name: voiceName })
  }

  /**
   * Tracks when a user selects a voice in the Voicemail step of the Call Flow Builder
   */
  voicemailVoiceSelected(voiceName: string) {
    void this.analytics.track('voicemail_voice_selected', { voice_name: voiceName })
  }

  /**
   * Tracks when a user selects a voice in the Phone Menu step of the Call Flow Builder
   */
  phoneMenuVoiceSelected(voiceName: string) {
    void this.analytics.track('phone_menu_voice_selected', { voice_name: voiceName })
  }

  /**
   * Tracks when a workspace integration is successfully connected
   *
   * @param workspaceIntegrationName - The name of the integration that was connected
   */
  workspaceIntegrationConnected(workspaceIntegrationName: string) {
    void this.analytics.track('workspace_integration_connected', {
      workspace_integration_name: workspaceIntegrationName,
    })
  }

  /**
   * Tracks when a workspace integration is disconnected
   *
   * @param workspaceIntegrationName - The name of the integration that was disconnected
   */
  workspaceIntegrationDisconnected(workspaceIntegrationName: string) {
    void this.analytics.track('workspace_integration_disconnected', {
      workspace_integration_name: workspaceIntegrationName,
    })
  }
}
